import React, { useState, useEffect } from 'react';
import { devLog, devWarn, devError } from '../../utils/devLogger';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { AppEmailTextField } from '../../customComponents/AppEmailTextField';
import { AppPwdTextField, PwdFieldType } from '../../customComponents/AppPwdTextField';
import { AppAuthCodeTextField } from '../../customComponents/AppAuthCodeTextField';
import { FormTitle } from '../../customComponents/FormTitle';
import PrimaryButton from '../../components/PrimaryButton';
import Modal from '../../components/Modal';
import CenteredLayout from '../../layout/CenteredLayout';
import PageContainerLayout from '../../layout/PageContainerLayout';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import FormContainer from '../../customComponents/FormContainer';
import { sendAuthCode, verifyAuthCode } from '../../lib/api/user/userApi';
import { useSignUp } from '../../context/SignUpContext';
import { useUserAuth } from '../../context/UserAuthContext';
import { useLang } from '../../context/LangContext';
import { useDevice } from '../../context/DeviceContext';

// 다국어 텍스트 객체
const signUpStep1Texts = {
  ko: {
    title: '회원가입',
    formTitle: '가입정보 입력',
    authButtonText: '인증하기',
    verifyButtonText: '인증확인',
    nextButton: '다음',
    authCodeSent: '인증번호가 발송되었습니다. 이메일을 확인해주세요.',
    authCodeSendFailed: '인증번호 발송에 실패했습니다. 다시 시도해주세요.',
    authCodeVerified: '인증번호가 확인되었습니다.',
    authCodeIncorrect: '인증번호가 맞지 않습니다.',
    authCodeInvalid: '인증번호가 올바르지 않습니다. 다시 확인해주세요.',
    emailDifferentModalTitle: '이메일 확인',
    emailDifferentModalMessage: '인증된 이메일과 입력된 이메일이 다릅니다.',
    verifiedEmailIs: '인증된 이메일은',
    willProceedWith: '으로 회원가입이 진행됩니다.',
    confirmButton: '확인',
    cancelButton: '취소'
  },
  en: {
    title: 'Sign Up',
    formTitle: 'Enter Registration Information',
    authButtonText: 'Verify',
    verifyButtonText: 'Confirm',
    nextButton: 'Next',
    authCodeSent: 'Verification code has been sent. Please check your email.',
    authCodeSendFailed: 'Failed to send verification code. Please try again.',
    authCodeVerified: 'Verification code has been confirmed.',
    authCodeIncorrect: 'Verification code is incorrect.',
    authCodeInvalid: 'Verification code is invalid. Please check again.',
    emailDifferentModalTitle: 'Email Confirmation',
    emailDifferentModalMessage: 'The verified email is different from the entered email.',
    verifiedEmailIs: 'The verified email is',
    willProceedWith: 'and registration will proceed with this email.',
    confirmButton: 'Confirm',
    cancelButton: 'Cancel'
  }
};

// SignUpContainer styles are now handled by PageContainerLayout

const FlexPageContainer = styled.div<{ $minHeight: string }>`
  display: flex;
  flex-direction: column;
  /* height: 100%; */
  min-height: ${({ $minHeight }) => $minHeight};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
`;

const NextButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: auto;
  padding: 24px 0 0 0;
`;

const ModalButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const CancelButton = styled(PrimaryButton)`
  background-color: #f5f5f5 !important;
  color: #666 !important;
  flex: 1;
`;

const ConfirmButton = styled(PrimaryButton)`
  flex: 1;
`;

function SignUpStep1() {
  const navigate = useNavigate();
  const { signUpData, updateSignUpData } = useSignUp();
  const { isLoggedIn } = useUserAuth();
  const { lang } = useLang();
  const device = useDevice();
  const texts = signUpStep1Texts[lang];
  
  // 앱바 높이를 임의로 설정 (모바일: 60px, 웹: 90px)
  const appbarHeight = device === 'mobile' ? 120 : 250;
  const minHeight = `calc(100vh - ${appbarHeight}px)`;
  
  const [email, setEmail] = useState(signUpData.email);
  const [verifiedEmail, setVerifiedEmail] = useState(signUpData.isAuthCodeVerified ? signUpData.email : ''); // 인증된 이메일 저장
  const [password, setPassword] = useState(signUpData.password);
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [authCode, setAuthCode] = useState(signUpData.authCode);
  const [isEmailValid, setIsEmailValid] = useState(signUpData.isEmailValid);
  const [isPasswordValid, setIsPasswordValid] = useState(signUpData.isPasswordValid);
  const [isPasswordConfirmValid, setIsPasswordConfirmValid] = useState(signUpData.isPasswordConfirmValid);
  const [isAuthCodeSent, setIsAuthCodeSent] = useState(signUpData.isAuthCodeVerified); // 인증이 완료되었다면 발송도 완료된 것
  const [isAuthCodeValid, setIsAuthCodeValid] = useState(signUpData.isAuthCodeVerified);
  const [isAuthCodeVerified, setIsAuthCodeVerified] = useState(signUpData.isAuthCodeVerified);
  const [showEmailDifferentModal, setShowEmailDifferentModal] = useState(false);

  // 이미 로그인된 상태라면 홈으로 리다이렉트
  useEffect(() => {
    if (isLoggedIn) {
      navigate('/');
    }
  }, [isLoggedIn, navigate]);

  // 에러 메시지 파싱 함수
  const parseErrorMessage = (error: any, defaultMessage: string): string => {
    // 중첩된 에러 구조 파싱
    if (error?.data?.error?.details?.details && Array.isArray(error.data.error.details.details)) {
      // validation 에러의 상세 메시지 추출
      const validationError = error.data.error.details.details[0];
      if (validationError?.message) {
        return validationError.message;
      }
    }
    
    if (error?.data?.error?.customMessage) {
      return error.data.error.customMessage;
    }
    
    if (error?.data?.message) {
      return error.data.message;
    }
    
    if (error?.message) {
      return error.message;
    }
    
    return defaultMessage;
  };

  const handleBack = () => {
    navigate(-1); // 이전 페이지로 이동
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEmail(e.target.value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPassword(e.target.value);
  };

  const handlePasswordConfirmChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPasswordConfirm(e.target.value);
  };

  const handleAuthCodeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setAuthCode(e.target.value);
    setIsAuthCodeVerified(false); // 인증번호가 변경되면 인증 확인 상태 초기화
  };

  const handleAuthRequest = async () => {
    try {
      const response = await sendAuthCode({ email });
      
      // 응답 데이터의 statusCode 확인 후 메시지 처리
      if (response.data.statusCode === 200) {
        toast.success(texts.authCodeSent);
        setIsAuthCodeSent(true);
        // 새로운 인증번호 발송 시 기존 인증 상태 초기화
        setAuthCode('');
        setIsAuthCodeValid(false);
        setIsAuthCodeVerified(false);
      } else {
        // 200이 아닌 응답 처리 - 서버 메시지 사용
        const errorMessage = response.data.message || texts.authCodeSendFailed;
        toast.error(errorMessage);
      }
    } catch (error: any) {
      let errorMessage = parseErrorMessage(error, texts.authCodeSendFailed);
      
      
      toast.error(errorMessage);
    }
  };

  const handleVerifyAuthCode = async () => {
    try {
      devLog('인증번호 확인 요청:', { email, authCode: Number(authCode) });
      const response = await verifyAuthCode({ email, authCode: Number(authCode) });
      devLog('인증번호 확인 응답:', response);
      
      // 응답 데이터의 statusCode 확인 후 메시지 처리
      if (response.data.statusCode === 200) {
        toast.success(texts.authCodeVerified);
        setIsAuthCodeVerified(true); // 인증 확인 완료 상태로 변경
        setVerifiedEmail(email); // 인증된 이메일 저장
      } else if (response.data.statusCode === 401) {
        // 401 에러 (인증번호 불일치) 처리
        toast.error(texts.authCodeIncorrect);
        setIsAuthCodeVerified(false);
      } else {
        // 기타 에러 응답 처리
        const errorMessage = response.data.message || texts.authCodeInvalid;
        toast.error(errorMessage);
        setIsAuthCodeVerified(false);
      }
    } catch (error: any) {
      devError('인증번호 확인 실패:', error);
      
      const errorMessage = parseErrorMessage(error, texts.authCodeInvalid);
      toast.error(errorMessage);
      setIsAuthCodeVerified(false); // 인증 실패 시 false로 재설정
    }
  };

  const handleNext = () => {
    // 인증된 이메일과 입력된 이메일이 다른 경우 모달로 확인
    if (isAuthCodeVerified && email !== verifiedEmail) {
      setShowEmailDifferentModal(true);
      return;
    }
    
    // 이메일이 같거나 인증이 안된 경우 바로 진행
    proceedToNextStep();
  };

  const proceedToNextStep = () => {
    // Context에 데이터 저장 - 인증된 이메일 사용
    const emailToSave = isAuthCodeVerified ? verifiedEmail : email;
    updateSignUpData({
      email: emailToSave,
      password,
      authCode,
      isEmailValid,
      isPasswordValid,
      isPasswordConfirmValid,
      isAuthCodeVerified,
    });
    
    devLog('다음 단계로:', { email: emailToSave, password, passwordConfirm });
    // Step2로 이동
    navigate('/signup/step2');
  };

  const handleEmailDifferentConfirm = () => {
    setShowEmailDifferentModal(false);
    proceedToNextStep();
  };

  const handleEmailDifferentCancel = () => {
    setShowEmailDifferentModal(false);
  };

  const isFormValid = isEmailValid && isPasswordValid && isPasswordConfirmValid && 
                     isAuthCodeValid && isAuthCodeSent && isAuthCodeVerified && // 인증번호 확인 완료 필수
                     password.trim() !== '' && passwordConfirm.trim() !== '' && authCode.trim() !== '' &&
                     verifiedEmail.trim() !== ''; // 인증된 이메일이 있어야 함

  return (
    <CenteredLayout>
      <ProjectAppbar 
        title={texts.title}
        showBackButton={true}
        onBack={handleBack}
      />
      <FormContainer>
        <PageContainerLayout>
          <FlexPageContainer $minHeight={minHeight}>
            <FormGroup>
              <FormTitle>{texts.formTitle}</FormTitle>
              <AppEmailTextField
                value={email}
                onChange={handleEmailChange}
                onValidationChange={setIsEmailValid}
                showAuthButton={true}
                onAuthRequest={handleAuthRequest}
                authButtonText={texts.authButtonText}
                language={lang}
                autoFocus={true}
              />
              
              {isAuthCodeSent && (
                <AppAuthCodeTextField
                  value={authCode}
                  onChange={handleAuthCodeChange}
                  onValidationChange={setIsAuthCodeValid}
                  showVerifyButton={true}
                  onVerifyRequest={handleVerifyAuthCode}
                  verifyButtonText={texts.verifyButtonText}
                  language={lang}
                  autoFocus={true}
                  readOnly={isAuthCodeVerified}
                />
              )}
              
              <AppPwdTextField
                value={password}
                onChange={handlePasswordChange}
                fieldType={PwdFieldType.PASSWORD}
                onValidationChange={setIsPasswordValid}
                language={lang}
                autoComplete="new-password"
              />

              <AppPwdTextField
                value={passwordConfirm}
                onChange={handlePasswordConfirmChange}
                fieldType={PwdFieldType.PASSWORD_CONFIRM}
                onValidationChange={setIsPasswordConfirmValid}
                originalPassword={password}
                language={lang}
                autoComplete="new-password"
              />
            </FormGroup>

            <NextButtonContainer>
              <PrimaryButton
                onClick={handleNext}
                disabled={!isFormValid}
              >
                {texts.nextButton}
              </PrimaryButton>
            </NextButtonContainer>
          </FlexPageContainer>
        </PageContainerLayout>
      </FormContainer>
      
      {/* 이메일 다름 확인 모달 */}
      <Modal
        isOpen={showEmailDifferentModal}
        onClose={handleEmailDifferentCancel}
        bgColor="#ffffff"
        header={texts.emailDifferentModalTitle}
        body={
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <p>{texts.emailDifferentModalMessage}</p>
            <p style={{ margin: '16px 0', fontSize: '16px', fontWeight: 'bold', color: '#1976d2' }}>
              {texts.verifiedEmailIs} <strong>{verifiedEmail}</strong> {texts.willProceedWith}
            </p>
          </div>
        }
        footer={
          <ModalButtonContainer>
            <CancelButton onClick={handleEmailDifferentCancel}>
              {texts.cancelButton}
            </CancelButton>
            <ConfirmButton onClick={handleEmailDifferentConfirm}>
              {texts.confirmButton}
            </ConfirmButton>
          </ModalButtonContainer>
        }
      />
    </CenteredLayout>
  );
}

export default SignUpStep1;
